/**
 * Burn Morels fruit mostly the spring after a fire (some into year two), so the map only
 * carries the current and previous fire seasons. Roll forward each January.
 */
export const WILDFIRE_BURN_YEARS = [2025, 2026] as const;

export type WildfireBurnYear = (typeof WILDFIRE_BURN_YEARS)[number];

/** Per-year visibility; all years switch together with the Burn Morels species. */
export type WildfireBurnYearVisibility = Record<WildfireBurnYear, boolean>;

export function initialWildfireBurnYearVisibility(): WildfireBurnYearVisibility {
  return Object.fromEntries(
    WILDFIRE_BURN_YEARS.map((year) => [year, false]),
  ) as WildfireBurnYearVisibility;
}

export function setAllWildfireBurnYearsVisible(
  show: boolean,
): WildfireBurnYearVisibility {
  return Object.fromEntries(
    WILDFIRE_BURN_YEARS.map((year) => [year, show]),
  ) as WildfireBurnYearVisibility;
}

export function isAnyWildfireBurnYearVisible(
  visibility: WildfireBurnYearVisibility,
): boolean {
  return WILDFIRE_BURN_YEARS.some((year) => visibility[year]);
}

export function visibleWildfireBurnYears(
  visibility: WildfireBurnYearVisibility,
): WildfireBurnYear[] {
  return WILDFIRE_BURN_YEARS.filter((year) => visibility[year]);
}

export const WILDFIRE_BURN_SOURCES_ORDERED: readonly WildfireBurnYear[] = WILDFIRE_BURN_YEARS;

export type WildfireBurnSourceConfig = {
  id: string;
  url: string;
  sourceLayer: string;
  /** Centroid points (one per burn) so small burns stay visible zoomed out. */
  pointsSourceLayer: string;
};

function envTilesUrl(year: WildfireBurnYear): string | undefined {
  const key = `REACT_APP_WILDFIRE_BURNS_${year}_TILES_URL` as keyof NodeJS.ProcessEnv;
  const v = process.env[key];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

const DEFAULT_TILES_URL: Partial<Record<WildfireBurnYear, string>> = {
  2025: 'mapbox://wildfoodnetwork.iyi5ay',
};

/** Years with a configured Mapbox tileset URL (env or default). */
export function getWildfireBurnsSourceForYear(
  year: WildfireBurnYear,
): WildfireBurnSourceConfig | null {
  const url = envTilesUrl(year) ?? DEFAULT_TILES_URL[year];
  if (!url) return null;
  return {
    id: `wildfire-burns-${year}-tiles`,
    url,
    sourceLayer: `wildfire_burns_${year}`,
    pointsSourceLayer: `wildfire_burn_points_${year}`,
  };
}

export const wildfireBurnsSourcesOrdered = WILDFIRE_BURN_SOURCES_ORDERED.map((year) => ({
  year,
  source: getWildfireBurnsSourceForYear(year),
})).filter(
  (entry): entry is { year: WildfireBurnYear; source: WildfireBurnSourceConfig } =>
    entry.source != null,
);

export const wildfireBurnsSources = Object.fromEntries(
  wildfireBurnsSourcesOrdered.map(({ year, source }) => [`wildfire_${year}`, source]),
) as Record<`wildfire_${WildfireBurnYear}`, WildfireBurnSourceConfig>;
